import { atom, selector } from 'recoil'
import {
  CaptureRateDatesResponse,
  CaptureRateResponse,
  QuarterPeriod,
  ReturnRateDatesResponse,
  ReturnRateResponse,
  VisitLengthDatesResponse,
  VisitLengthResponse,
} from 'src/common-services'
import { footfallClient } from 'src/clients'
import { DEFAULT_LRU_SIZE, keyFactory, retryAtom, uncachedSelector } from 'src/utils/recoil'
import { venueMatch } from 'src/atoms/search'
import { ifNotFound } from 'src/utils/ajax'
import { isPaidUser } from 'src/atoms/user'
import { DropdownOption } from 'src/components/dropdown'

export * from './trends'
export * from './patterns-hour'
export * from './patterns-weekday'
export * from './cross-visits'
export * from './area'

const key = keyFactory('insights-footfall')

export const DEFAULT_RETURN_RATE_SELECTED_RANGE = 12

export const showVisitLengthDates = atom<boolean>({
  key: key('show-visit-length-dates'),
  default: true,
})

export const visitLengthDates = selector<VisitLengthDatesResponse>({
  key: key('visit-length-dates'),
  get: ({ get }) => {
    get(retryAtom)
    return footfallClient.getVisitLengthDates()
  },
})

export const visitLengthDatesOptions = selector<DropdownOption[]>({
  key: key('visit-length-dates-options'),
  get: ({ get }) => {
    const visitLengthDatesValue = get(visitLengthDates)
    return visitLengthDatesValue.periods.map((period, index) => ({
      id: index.toString(),
      label: period.quarter,
    }))
  },
})

export const visitLengthInternalSelectedDateIndex = atom<number | undefined>({
  key: key('visit-length-internal-selected-date-index'),
  default: undefined,
})

export const visitLengthSelectedDateIndex = selector<number>({
  key: key('visit-length-selected-date-index'),
  get: ({ get }) => {
    const visitLengthDatesValue = get(visitLengthDates)
    const visitLengthInternalSelectedDateIndexValue = get(visitLengthInternalSelectedDateIndex)

    if (
      visitLengthInternalSelectedDateIndexValue !== undefined &&
      visitLengthDatesValue.periods[visitLengthInternalSelectedDateIndexValue]
    ) {
      return visitLengthInternalSelectedDateIndexValue
    }
    return visitLengthDatesValue.periods.length - 1
  },
})

export const visitLengthSelectedDate = selector<QuarterPeriod>({
  key: key('visit-length-selected-date'),
  get: ({ get }) => {
    const visitLengthDatesValue = get(visitLengthDates)
    const visitLengthSelectedDateIndexValue = get(visitLengthSelectedDateIndex)
    return visitLengthDatesValue.periods[visitLengthSelectedDateIndexValue]
  },
})

export const visitLength = uncachedSelector<VisitLengthResponse | undefined>({
  key: key('visit-length'),
  get: ({ get }) => {
    get(retryAtom)

    if (!get(isPaidUser)) {
      return { visitLengthP25: 7, visitLengthP50: 20, visitLengthP75: 52 }
    }

    const visitLengthSelectedDateValue = get(visitLengthSelectedDate)
    const venue = get(venueMatch)
    if (!venue) {
      return undefined
    }
    return ifNotFound(
      footfallClient.getVisitLength({
        start: visitLengthSelectedDateValue.start,
        venueId: venue.venueId,
      }),
      undefined
    )
  },
  lru: DEFAULT_LRU_SIZE,
})

export const showCaptureRateDates = atom<boolean>({
  key: key('show-capture-rate-dates'),
  default: true,
})

export const captureRateDates = selector<CaptureRateDatesResponse>({
  key: key('capture-rate-dates'),
  get: ({ get }) => {
    get(retryAtom)
    return footfallClient.getCaptureRateDates()
  },
})

export const captureRateDatesOptions = selector<DropdownOption[]>({
  key: key('capture-rate-dates-options'),
  get: ({ get }) => {
    const captureRateDatesValue = get(captureRateDates)
    return captureRateDatesValue.periods.map((period, index) => ({
      id: index.toString(),
      label: period.quarter,
    }))
  },
})

export const captureRateInternalSelectedDateIndex = atom<number | undefined>({
  key: key('capture-rate-internal-selected-date-index'),
  default: undefined,
})

export const captureRateSelectedDateIndex = selector<number>({
  key: key('capture-rate-selected-date-index'),
  get: ({ get }) => {
    const captureRateDatesValue = get(captureRateDates)
    const captureRateInternalSelectedDateIndexValue = get(captureRateInternalSelectedDateIndex)

    if (
      captureRateInternalSelectedDateIndexValue !== undefined &&
      captureRateDatesValue.periods[captureRateInternalSelectedDateIndexValue]
    ) {
      return captureRateInternalSelectedDateIndexValue
    }
    return captureRateDatesValue.periods.length - 1
  },
})

export const captureRateSelectedDate = selector<QuarterPeriod>({
  key: key('capture-rate-selected-date'),
  get: ({ get }) => {
    const captureRateDatesValue = get(captureRateDates)
    const captureRateSelectedDateIndexValue = get(captureRateSelectedDateIndex)
    return captureRateDatesValue.periods[captureRateSelectedDateIndexValue]
  },
})

export const captureRate = uncachedSelector<CaptureRateResponse | undefined>({
  key: key('capture-rate'),
  get: ({ get }) => {
    get(retryAtom)
    if (!get(isPaidUser)) {
      return { captureRateP25: 0.528, captureRateP50: 0.625, captureRateP75: 0.71 }
    }
    const captureRateSelectedDateValue = get(captureRateSelectedDate)
    const venue = get(venueMatch)
    if (!venue) {
      return undefined
    }
    return ifNotFound(
      footfallClient.getCaptureRate({
        start: captureRateSelectedDateValue.start,
        venueId: venue.venueId,
      }),
      undefined
    )
  },
  lru: DEFAULT_LRU_SIZE,
})

export const returnRateDates = selector<ReturnRateDatesResponse>({
  key: key('return-rate-dates'),
  get: ({ get }) => {
    get(retryAtom)
    return footfallClient.getReturnRateDates()
  },
})

export const showReturnRateSelectedRange = atom<boolean>({
  key: key('show-return-rate-range'),
  default: true,
})

export const returnRateSelectedRange = atom<number>({
  key: key('return-rate-range'),
  default: DEFAULT_RETURN_RATE_SELECTED_RANGE,
})

export const returnRateSelectedDateList = selector<string[]>({
  key: key('return-rate-selected-date-list'),
  get: ({ get }) => {
    const returnRateSelectedRangeValue = get(returnRateSelectedRange)
    const returnRateDatesValue = get(returnRateDates)
    let fromIndex = returnRateDatesValue.periods.length - returnRateSelectedRangeValue
    if (fromIndex < 0) {
      fromIndex = 0
    }
    return returnRateDatesValue.periods.slice(fromIndex)
  },
})

export const returnRateSelectedDate = selector<string[]>({
  key: key('return-rate-selected-date'),
  get: ({ get }) => {
    const returnRateSelectedRangeValue = get(returnRateSelectedRange)
    const returnRateDatesValue = get(returnRateDates)
    const end = returnRateDatesValue.periods[returnRateDatesValue.periods.length - 1]
    let fromIndex = returnRateDatesValue.periods.length - returnRateSelectedRangeValue
    if (fromIndex < 0) {
      fromIndex = 0
    }
    const start = returnRateDatesValue.periods[fromIndex]
    return [start, end]
  },
})

export const returnRate = uncachedSelector<ReturnRateResponse | undefined>({
  key: key('return-rate'),
  get: ({ get }) => {
    get(retryAtom)
    if (!get(isPaidUser)) {
      return [
        { date: '2020-06-01', frac: 0.43 },
        { date: '2020-07-01', frac: 0.45 },
        { date: '2020-08-01', frac: 0.39 },
        { date: '2020-09-01', frac: 0.38 },
        { date: '2020-10-01', frac: 0.4 },
        { date: '2020-11-01', frac: 0.43 },
        { date: '2020-12-01', frac: 0.4 },
        { date: '2021-01-01', frac: 0.38 },
        { date: '2021-02-01', frac: 0.33 },
        { date: '2021-03-01', frac: 0.4 },
        { date: '2021-04-01', frac: 0.34 },
        { date: '2021-05-01', frac: 0.29 },
      ]
    }
    const [start, end] = get(returnRateSelectedDate)
    const venue = get(venueMatch)
    if (!venue) {
      return undefined
    }
    return ifNotFound(
      footfallClient.getReturnRate({
        start,
        end,
        venueId: venue.venueId,
      }),
      undefined
    )
  },
  lru: DEFAULT_LRU_SIZE,
})
